import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/PricingPage.module.css'; 
import videoBackground from '../styles/background.mp4';

const plans = [
  {
    name: "Basic",
    priceMonthly: "$24,99",
    priceAnnual: "$14,99 and 2 months free",
    summary: "Essential and Efficient Fleet Management for Small to Mid-Sized Operations",
    details: `
    BASIC Plan – Essential and Efficient Fleet Management for Small to Mid-Sized Operations

    The BASIC plan is designed for small to mid-sized trucking businesses that require a streamlined yet powerful solution for managing loads, drivers, and essential operations. Whether you're just starting out or managing a compact fleet, this plan provides all the critical tools you need to maintain efficiency, transparency, and control over your business operations.

   🚛 Load Management – Simplify Your Load Operations
    - Load Entry – Seamlessly add new loads, including pickup and delivery details, assigned trucks, and relevant documentation.
    - Load Scheduler – A built-in scheduling tool that enables you to view, edit, and manage all your planned loads in one place, ensuring efficient route planning.
    - Load Tracker – Easily monitor your ongoing loads with real-time updates, keeping you informed of their status at all times.

  📊 Operations – Manage Key Business Resources
    - Drivers – Add and manage up to 10 drivers within your fleet.
    - Fleet Management – Maintain up to 10 trucks for optimized dispatching and load allocation.
    - Trailers – Register and keep track of trailers used for different shipments.
    - **Shippers & Consignees** – Store and manage key details about shippers and consignees to facilitate seamless logistics coordination.
    - **Brokers** – Maintain records of brokers you work with to streamline business partnerships and contracts.

    💰 Payroll – Streamline Driver Compensation
    - Easily create and manage driver payroll based on completed trips, ensuring accurate and timely compensation.
    - Generate and store payroll reports to maintain full financial oversight.

   ⚙️ Plan Limits & Additional Benefits:
    - Maximum of 10 drivers and 10 trucks in your fleet.
    - Includes 1 Dispatcher and 1 Accounting user to assist in managing operations.
    - If you choose the **annual subscription**, you receive 3 additional drivers and 3 extra trucks at no extra cost!

   💲 Subscription Pricing & Discounts
    - Monthly Subscription: $24,99/month
    - Annual Subscription: $149,99/year (Only €14,99/month – Pay for 10 months instead of 12!)

   📞 24/7 Customer Support – Always Here for You
    - Enjoy round-the-clock professional support to ensure uninterrupted operations. Our team is available anytime to assist with technical issues, troubleshooting, or general inquiries.
    Why Choose the BASIC Plan?
✔️ Cost-effective solution for small fleets and independent operators.
✔️ User-friendly interface for seamless load and fleet management.
✔️ Scalable structure to accommodate future business growth.
✔️ Reliable support to keep your operations running smoothly.

The BASIC plan is your gateway to efficient trucking management—empowering your business with essential tools at an affordable price! 🚛✨`
  },
  {
    name: "Standard",
    priceMonthly: "$39,99",
    priceAnnual: "$24,99 and 2 months free",
    summary: "Advanced Fleet & Financial Management for Growing Trucking Businesses",
    details: ` STANDARD Plan – Advanced Fleet & Financial Management for Growing Trucking Businesses
    The STANDARD plan is tailored for trucking businesses that require enhanced financial and operational management beyond the basics.
    In addition to everything included in the BASIC plan, this package introduces powerful payroll tracking, fuel reporting, and expanded fleet capabilities. With greater flexibility and detailed insights, this plan is ideal for companies looking to scale their operations efficiently.
🚛 Load Management – Full Control Over Your Loads
Load Entry – Easily input new loads with pickup and delivery details, assigned drivers, and associated vehicles.
Load Scheduler – Manage, reschedule, and edit loads with an intuitive planning interface.
Load Tracker – Monitor all active loads, ensuring complete operational visibility.
📊 Operations – Manage Your Fleet & Workforce with Ease
Drivers – Expand your team by adding up to 15 drivers to your system.
Fleet Management – Maintain up to 15 trucks with full tracking and dispatching capabilities.
Trailers – Register and manage trailers to optimize load handling.
Shippers & Consignees – Store and manage all essential shipping contacts for smooth coordination.
Brokers – Keep a comprehensive record of all brokers and manage contracts efficiently.
💰 Payroll – Advanced Salary Management for Your Drivers
Payroll Processing – Generate accurate payrolls based on completed trips.
NEW: Payroll Manager – View payroll history and track all previous salary payments.
Inactive Drivers Overview – Keep records of former employees who are no longer working with your company for better workforce tracking.
⛽ Fuel Reports – Gain Full Control Over Fuel Costs
Fuel Expense Tracking – Log and monitor fuel expenses to optimize cost efficiency.
Export Fuel Reports – Generate and download fuel reports in Excel format, making it easy to analyze data and share insights.
⚙️ Plan Limits & Additional Benefits
Maximum of 15 drivers and 15 trucks in your fleet.
Includes 2 Dispatchers and 2 Accounting users for streamlined business management.
Annual Plan Bonus: If you choose the annual subscription, you receive:
3 additional drivers and 3 extra trucks at no cost.
1 additional Dispatcher and 1 extra Accounting user for free!
💲 Subscription Pricing & Discounts
Monthly Subscription: $39,99/month
Annual Subscription: $249,99/year (Only $24,99/month – Pay for 10 months instead of 12!)
📞 24/7 Customer Support – Assistance Anytime, Anywhere
Enjoy round-the-clock professional support to ensure smooth operations. Our expert team is available 24/7 to assist with any technical difficulties, inquiries, or troubleshooting needs.
Why Upgrade to the STANDARD Plan?
✔️ Enhanced payroll tracking for better financial management.
✔️ Expanded driver and fleet capacity to support business growth.
✔️ Fuel reporting & cost analysis to optimize operational expenses.
✔️ More dispatchers & accountants to manage workloads efficiently.
✔️ Exclusive annual plan benefits with free upgrades!

🚛 The STANDARD Plan is the perfect choice for trucking businesses looking to scale efficiently, streamline payroll management, and take control of fuel expenses—all at an unbeatable value! 🚛
    `
  },
  {
    name: "Premium",
    priceMonthly: "$64,99",
    priceAnnual: "$44,99 and 2 months free",
    summary: "Advanced Automation & Comprehensive Financial Control",
    details: ` PREMIUM Plan – Advanced Automation & Comprehensive Financial Control for Growing Trucking Businesses
    The PREMIUM plan is built for trucking businesses that require automation, seamless invoicing, advanced payroll customization , and real-time business insights. This plan goes beyond standard fleet management, providing powerful tools to automate workflows, enhance financial tracking, and improve decision-making.

With PREMIUM, you’ll benefit from everything in the STANDARD plan, plus exclusive features that boost efficiency, accuracy, and scalability—allowing you to focus on growing your trucking business.

🚛 Load Management – Smarter & Faster Load Entry
Load Entry with Auto-Fill – Upload a document, and the system will automatically extract relevant details to populate the load form—eliminating manual data entry and saving valuable time.
Load Scheduler – Effortlessly edit, reschedule, and manage all active and upcoming loads.
Load Tracker – Keep track of all loads in real-time to ensure smooth operations.
📊 Operations – Expanded Fleet & Team Capacity
Drivers – Manage up to 25 drivers, ensuring your business is fully equipped for expansion.
Fleet Management – Track and manage up to 25 trucks for optimized dispatching.
Trailers – Organize trailers and ensure they’re allocated correctly.
Shippers & Consignees – Store and manage all shipping-related contacts for easy coordination.
Brokers – Maintain a detailed database of brokers and contracts.
💰 Payroll – Fully Customizable Salary Management
Payroll Processing – Easily generate and track driver payments based on completed trips.
Payroll Manager – View payroll history, track old payments, and ensure financial transparency.
Inactive Drivers Overview – Maintain records of past employees.
Company Branding for Payroll Documents
Upload your company logo to personalize payroll records.
Add a custom footer with additional company details.
Edit and modify these elements at any time for flexibility and professional presentation.
📑 Invoice Management – Automate Your Billing Process
NEW: Invoiced Loads – Instantly view all completed loads in a dedicated invoicing dashboard.
Email Invoices – Send invoices directly to clients via email, saving time and effort.
Download PDF Invoices – Export invoices as PDF files for record-keeping and financial reporting.
📊 Business Insights – Advanced Overview Dashboard
NEW: Overview Dashboard – Get a real-time view of your entire trucking business in one place.
Monitor Key Metrics – Track revenue, fuel costs, payroll expenses, completed loads, and more.
Task Management & Progress Tracking – Assign, manage, and track tasks for drivers, dispatchers, and other team members.
⚙️ Plan Limits & Additional Benefits
Manage 25 drivers and 25 trucks with full operational control.
Expand your team with 4 Dispatchers and 4 Accounting users to handle increased workload.
Annual Plan Bonus: If you choose the annual subscription, you receive:
5 additional drivers and 5 extra trucks at no cost.
2 additional Dispatchers and 2 extra Accounting users for free!
💲 Subscription Pricing & Discounts
Monthly Subscription: $64,99/month
Annual Subscription: $449,99/year (Only $44,99/month – Pay for 10 months instead of 12!)
📞 24/7 Dedicated Customer Support – Always Here for You
Get priority support with our expert customer service team available 24/7 to assist with any technical issues, billing inquiries, or feature-related questions.
Why Upgrade to the PREMIUM Plan?
✔️ Automated Load Entry – No more manual data entry; simply upload a document, and the system fills in the details.
✔️ Fully branded payroll – Customize payroll documents with your logo & footer for a professional touch.
✔️ Integrated invoicing – View, email, and download invoices directly from the system.
✔️ Advanced analytics – Gain real-time insights into your trucking business.
✔️ Task & progress tracking – Stay organized and ensure smooth operations.
✔️ Exclusive annual plan benefits – Get free additional drivers, trucks, dispatchers, and accountants!

🚛 The PREMIUM Plan is the ultimate solution for trucking businesses that want to scale, automate, and gain full financial control—all while maintaining operational efficiency! 🚛
    `
  },
  {
    name: "Ultimate",
    priceMonthly: "$94,99",
    priceAnnual: "$74,99 and 2 months free",
    summary: "The Most Powerful, Automated & Scalable Solution for Enterprise-Level Trucking Businesses",
    details: `ULTIMATE Plan – The Most Powerful, Automated & Scalable Solution for Enterprise-Level Trucking Businesses
    The ULTIMATE plan is the pinnacle of trucking management technology, designed for businesses that demand maximum automation, real-time tracking, advanced accounting tools, and seamless communication with drivers and brokers.

With this plan, you not only get everything included in the PREMIUM package, but you also unlock next-level tools like automated factoring, in-depth dashboards, a dedicated driver mobile app, real-time location sharing, and a built-in communication center—making it the most comprehensive solution on the market.

🚛 Load Management – Ultimate Control Over Your Operations
Load Entry with Auto-Fill – Upload documents and let the system automatically populate load details.
Load Scheduler & Tracker – Full visibility over scheduled, completed, canceled, and unpaid loads.
NEW: Load Dashboard – A dedicated dashboard where you can see every single detail of your loads, including:
Fuel reports
Rate per mile
Completed vs. canceled loads
Paid vs. unpaid loads
📊 Driver & Fleet Management – Full Operational Insights
NEW: Driver Dashboard – Get an in-depth view of each driver’s performance, miles driven, fuel consumption, completed loads, and payroll status.
NEW: Truck Dashboard – Monitor truck-specific details like total miles driven, fuel efficiency, maintenance logs, and load assignments.
Manage Up to:
50 drivers and 50 trucks
7 Dispatchers and 7 Accounting users
💰 Accounting Manager – Automate Your Invoicing & Factoring
NEW: Factoring Automation – Send invoiced loads directly to factoring companies with a single click.
Upload & Attach Documents – Add PDFs or images to invoices before sending.
Automated Email Delivery – Send invoices to the factoring company’s pre-set email and any additional recipients you specify.
Save Email Preferences – Set email contacts once and reuse them for future factoring submissions effortlessly.
📡 Real-Time Driver Tracking & Location Sharing
Driver Location Tracking – View each driver’s live location directly in the system.
Google Maps Integration – See the real-time position of every driver inside the Overview Dashboard.
NEW: Share Tracking Link with Brokers – Generate a secure, expiring link to share with brokers, allowing them to track load progress without calling dispatchers.
Flexible Link Expiration – Extend or delete tracking links as needed.
📱 Dedicated Mobile App for Drivers
Driver Account Management – Create and manage driver profiles directly through the mobile app.
NEW: In-App Document Scanning – Drivers can scan and send documents as PDFs directly to dispatchers, eliminating the need for third-party apps.
NEW: Direct Messaging with Drivers – Instant chat with drivers via the Communication Center (supports emojis and multimedia).
💬 Communication Center – Real-Time Messaging for Seamless Collaboration
Integrated Chat System – Communicate instantly with drivers through the web platform.
Emoji & Multimedia Support – Drivers can send and receive text messages, images, and documents.
Improved Dispatch Efficiency – Reduce unnecessary phone calls and streamline communication.
⚙️ Plan Limits & Additional Benefits
Manage 50 drivers and 50 trucks with complete tracking.
Includes 7 Dispatchers and 7 Accounting users for comprehensive business support.
Annual Plan Bonus: If you choose the annual subscription, you receive:
15 additional drivers and 15 extra trucks for free.
3 additional Dispatchers and 3 extra Accounting users at no extra cost.
💲 Subscription Pricing & Discounts
Monthly Subscription: $94,99/month
Annual Subscription: $749,99/year (Only $74,99/month – Pay for 10 months instead of 12!)
📞 24/7 VIP Support – Premium Assistance for High-Volume Operations
Get priority, enterprise-grade support with dedicated specialists available 24/7 for troubleshooting, onboarding assistance, and feature optimizations.
Why Upgrade to the ULTIMATE Plan?
✔️ Automate factoring – Send invoices and documents to factoring companies with one click.
✔️ Advanced dashboards – Get a complete overview of your loads, drivers, and trucks.
✔️ Live driver tracking – Monitor real-time locations and share tracking links with brokers.
✔️ Dedicated mobile app – Empower drivers with instant document scanning & messaging.
✔️ Built-in Communication Center – No more unnecessary calls—chat directly with drivers.
✔️ Exclusive annual plan bonuses – Get free additional drivers, trucks, and dispatchers!

🚛 The ULTIMATE Plan is the most powerful, fully automated solution for trucking businesses that demand top-tier efficiency, real-time tracking, and seamless financial management—all in one platform! 🚛
    `
  },
  {
    name: "Simple Statement",
    priceMonthly: "$12,99",
    priceAnnual: "$9,99 and 2 months free",
    summary: "Simplified load tracking and management at the lowest cost.",
    details: `
      The **Simple Statement** plan is designed for small trucking businesses that need a lightweight solution for managing loads and tracking their fleet. 
      Ideal for independent operators or those who need a **cost-effective** yet efficient solution.

      🚛 **Features**:
      
      - Automated Expense Statements
      - Simple Payroll Summary

      💲 **Pricing**:
      - Monthly: **$7.99**
      - Annual: **$3.99/month** (billed annually)

      📞 **Support**:
      - Email Support
      - 24/7 Support
    `
  },
  {
    name: "Custom",
    priceMonthly: "Custom Pricing",
    priceAnnual: "Tailored to Your Needs",
    summary: "A fully customizable plan for enterprises with unique needs.",
    details: `
      The **Custom Plan** is built for enterprises and growing trucking businesses that require **personalized features** and **scalable solutions**.
      Work directly with our team to create a plan that fits your **exact requirements**.

      🚛 **Customizable Features**:
      - Unlimited Loads
      - Unlimited Drivers & Trucks
      - AI-Driven Fleet Optimization
      - Advanced Accounting & Payroll
      - Dedicated Customer Support

      💲 **Pricing**:
      - Custom Quote Based on Features & Business Size

      📞 **Support**:
      - **Dedicated Account Manager**
      - **Priority 24/7 Support**
      - **Personalized Training**
    `
  }
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);

  return (
    <div className={styles.pricingPageContainer}>
       <video className={styles.pricingPageVideoBackground} autoPlay loop muted>
             <source src={videoBackground} type="video/mp4" />
             Your browser does not support the video tag.
           </video>

      {/* Header */}
      <header className={styles.pricingPageheader}>
        <div onClick={() => navigate('/')} className={styles.pricingPagelogo}>Full Trucking Service</div>
        <nav>
          <ul>
            <li onClick={() => navigate('/about')}>About Us</li>
            <li onClick={() => navigate('/services')}>Services</li>
            <li onClick={() => navigate('/contact')}>Contact</li>
            <li onClick={() => navigate('/pricing')}>Register</li>
          </ul>
        </nav>
      </header>

      <h1 className={styles.pricingPagetitle}>Choose Your Plan</h1>

      <div className={styles.pricingPageplansContainer}>
        {plans.map((plan, index) => (
          <div key={index} className={styles.pricingPageplanCard}>
            <h2>{plan.name}</h2>
            <p>{plan.summary}</p>
            <div className={styles.pricingPageprice}>Monthly: {plan.priceMonthly}</div>
            <div className={styles.pricingPagepriceAnnual}>Annual: {plan.priceAnnual}</div>
            <button onClick={() => navigate('/register', { state: { plan: plan.name } })}>
              Select {plan.name}
            </button>
            <button onClick={() => setSelectedPlan(plan)}>See More</button>
          </div>
        ))}
      </div>

      {/* Modal for detailed plan info */}
      {selectedPlan && (
        <div className={styles.pricingPagefullScreenModal}>
          <div className={styles.pricingPagemodalContent}>
          <button className={styles.pricingPagecloseButton} onClick={() => setSelectedPlan(null)}>✖</button>
            <h2>{selectedPlan.name} Plan</h2>
            <p dangerouslySetInnerHTML={{ __html: selectedPlan.details.replace(/\n/g, '<br/>') }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
